import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Doc, Id } from '@/convex/_generated/dataModel';
import { wasCompletedToday, isOverdue } from '@/lib/task-helpers';
import { 
  shouldAppearInInbox, 
  shouldAppearInToday, 
  shouldAppearInThisWeek,
  shouldAppearInThisMonth,
  shouldAppearInBacklog
} from '@/lib/list-rule-helpers';

interface TaskState {
  // Raw data from Convex
  allTasks: Doc<'normalTasks'>[] | undefined;
  
  // Computed values (derived from allTasks)
  todayTasks: Doc<'normalTasks'>[] | undefined;
  inboxTasks: Doc<'normalTasks'>[] | undefined;
  priorityTasks: {
    thisWeek: Doc<'normalTasks'>[];
    thisMonth: Doc<'normalTasks'>[];
    backlog: Doc<'normalTasks'>[];
  } | undefined;
  scheduledTasks: Doc<'normalTasks'>[] | undefined;
  allPageTasks: Doc<'normalTasks'>[] | undefined;
  recurringTasks: Doc<'normalTasks'>[] | undefined;
  completedTodayTasks: Doc<'normalTasks'>[] | undefined;
  
  // Actions
  setAllTasks: (tasks: Doc<'normalTasks'>[] | undefined) => void;
  
  // Query-like selectors (mimicking Convex queries)
  getTaskById: (taskId: string) => Doc<'normalTasks'> | undefined;
  getTasksBySegment: (segmentId: string) => Doc<'normalTasks'>[];
  getTasksByCategory: (categoryId: string) => Doc<'normalTasks'>[];
  getTasksByProject: (projectId: string) => Doc<'normalTasks'>[];
  getTasksByDate: (date: string) => Doc<'normalTasks'>[];
  getTasksByDateAndSegment: (date: string, segmentId: Id<'daySegments'>) => Doc<'normalTasks'>[];
  getOverdueTasks: () => Doc<'normalTasks'>[];
  getUnscheduledTasks: () => Doc<'normalTasks'>[];
  getTasksWithoutProject: () => Doc<'normalTasks'>[];
  getTasksWithoutCategory: () => Doc<'normalTasks'>[];
}

// Helper function to compute derived task lists
function computeDerivedTasks(tasks: Doc<'normalTasks'>[]) {
  const todayTasks = tasks.filter(shouldAppearInToday);
  const inboxTasks = tasks.filter(shouldAppearInInbox);
  
  const priorityTasks = {
    thisWeek: tasks.filter(shouldAppearInThisWeek),
    thisMonth: tasks.filter(shouldAppearInThisMonth),
    backlog: tasks.filter(shouldAppearInBacklog),
  };
  
  const scheduledTasks = tasks
    .filter(task => 
      task.dueAt && 
      !task.recurringTaskTemplateId && 
      !task.completed
    )
    .sort((a, b) => a.dueAt!.localeCompare(b.dueAt!));
  
  const allPageTasks = tasks.filter(task => !task.completed);
  const recurringTasks = tasks.filter(task => 
    task.recurringTaskTemplateId && !task.completed
  );
  const completedTodayTasks = tasks.filter(task => wasCompletedToday(task));
  
  return {
    todayTasks,
    inboxTasks,
    priorityTasks,
    scheduledTasks,
    allPageTasks,
    recurringTasks,
    completedTodayTasks,
  };
}

// Helper to create selector functions
function createSelectors(get: () => TaskState) {
  return {
    getTaskById: (taskId: string) => {
      const tasks = get().allTasks;
      return tasks?.find(task => task._id === taskId);
    },
    
    getTasksBySegment: (segmentId: string) => {
      const tasks = get().allTasks;
      if (!tasks) return [];
      return tasks.filter(task => task.segmentId === segmentId && !task.completed);
    },
    
    getTasksByCategory: (categoryId: string) => {
      const tasks = get().allTasks;
      if (!tasks) return [];
      return tasks.filter(task => task.categoryId === categoryId && !task.completed);
    },
    
    getTasksByProject: (projectId: string) => {
      const tasks = get().allTasks;
      if (!tasks) return [];
      return tasks.filter(task => task.projectId === projectId && !task.completed);
    },
    
    getOverdueTasks: () => {
      const tasks = get().allTasks;
      if (!tasks) return [];
      return tasks.filter(task => isOverdue(task));
    },
    
    getTasksByDate: (date: string) => {
      const tasks = get().allTasks;
      if (!tasks) return [];
      return tasks.filter(task => task.dueAt === date);
    },
    
    getTasksByDateAndSegment: (date: string, segmentId: Id<'daySegments'>) => {
      const tasks = get().allTasks;
      if (!tasks) return [];
      return tasks.filter(task => 
        task.dueAt === date && task.segmentId === segmentId
      );
    },
    
    getUnscheduledTasks: () => {
      const tasks = get().allTasks;
      if (!tasks) return [];
      return tasks.filter(task => !task.dueAt && !task.completed);
    },
    
    getTasksWithoutProject: () => {
      const tasks = get().allTasks;
      if (!tasks) return [];
      return tasks.filter(task => !task.projectId && !task.completed);
    },
    
    getTasksWithoutCategory: () => {
      const tasks = get().allTasks;
      if (!tasks) return [];
      return tasks.filter(task => !task.categoryId && !task.completed);
    },
  };
}

// Helper to create initial state
function createInitialState() {
  return {
    allTasks: undefined,
    todayTasks: undefined,
    inboxTasks: undefined,
    priorityTasks: undefined,
    scheduledTasks: undefined,
    allPageTasks: undefined,
    recurringTasks: undefined,
    completedTodayTasks: undefined,
  };
}

export const useTaskStore = create<TaskState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    ...createInitialState(),
    
    // Actions
    setAllTasks: (tasks) => {
      set(() => {
        if (!tasks) {
          return createInitialState();
        }
        
        const derivedTasks = computeDerivedTasks(tasks);
        
        return {
          allTasks: tasks,
          ...derivedTasks,
        };
      });
    },
    
    // Selectors
    ...createSelectors(get),
  }))
);

// Selector hooks for optimized re-renders
export const useTodayTasks = () => useTaskStore((state) => state.todayTasks);
export const useInboxTasks = () => useTaskStore((state) => state.inboxTasks);
export const usePriorityTasks = () => useTaskStore((state) => state.priorityTasks);
export const useScheduledTasks = () => useTaskStore((state) => state.scheduledTasks);
export const useAllPageTasks = () => useTaskStore((state) => state.allPageTasks);
export const useRecurringTasks = () => useTaskStore((state) => state.recurringTasks);
export const useCompletedTodayTasks = () => useTaskStore((state) => state.completedTodayTasks);

// Dynamic selector hooks
export const useTaskById = (taskId: string) => useTaskStore((state) => state.getTaskById(taskId));
export const useTasksBySegment = (segmentId: string) => useTaskStore((state) => state.getTasksBySegment(segmentId));
export const useTasksByCategory = (categoryId: string) => useTaskStore((state) => state.getTasksByCategory(categoryId));
export const useTasksByProject = (projectId: string) => useTaskStore((state) => state.getTasksByProject(projectId));
export const useTasksByDate = (date: string) => useTaskStore((state) => state.getTasksByDate(date));
export const useTasksByDateAndSegment = (date: string, segmentId: Id<'daySegments'>) => 
  useTaskStore((state) => state.getTasksByDateAndSegment(date, segmentId));
export const useOverdueTasks = () => useTaskStore((state) => state.getOverdueTasks());
export const useUnscheduledTasks = () => useTaskStore((state) => state.getUnscheduledTasks());
export const useTasksWithoutProject = () => useTaskStore((state) => state.getTasksWithoutProject());
export const useTasksWithoutCategory = () => useTaskStore((state) => state.getTasksWithoutCategory());